<template>
  <v-card>
    <v-card-title> Template d'Intervention </v-card-title>

    <v-card-text>
      <!-- Product Type -->
      <v-select v-model="template.productType" :items="productTypes" label="Type de Produit" item-title="label"
        item-value="value" :rules="[(v) => !!v || 'Required']" />

      <v-divider class="my-6" />

      <!-- Items -->
      <div class="d-flex justify-space-between align-center mb-2">
        <h3>Etapes d'Intervention</h3>
      </div>
      <v-card v-for="(item, index) in template.items" :key="index" class="mb-4 pa-4" variant="outlined">
        <div class="d-flex justify-space-between align-center mb-3">
          <strong>Etape {{ index as number + 1 }}</strong>

          <div>
            <v-btn icon size="small" @click="moveUp(index)">
              <v-icon>mdi-arrow-up</v-icon>
            </v-btn>

            <v-btn icon size="small" @click="moveDown(index)">
              <v-icon>mdi-arrow-down</v-icon>
            </v-btn>

            <v-btn icon size="small" color="error" @click="removeItem(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <v-text-field v-model="item.label" label="Label" :rules="[(v) => !!v || 'Required']" />

        <v-select v-model="item.itemType" :items="itemTypes" label="Type d'étape" item-title="title" item-value="value" />

        <v-checkbox v-model="item.required" label="Requis" />
      </v-card>
    </v-card-text>
    <div class="d-flex justify-center">
      <v-btn color="primary" @click="addItem"> Ajouter une etape </v-btn>
      <v-btn color="white" @click="router.push({ name: 'interventiontemplates' })"> Retour </v-btn>
    </div>

    <v-card-actions class="justify-end">
      <v-btn color="primary" :loading="loading" @click="submit"> Sauvegarder </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store";
import { InterventionItemType, InterventionTemplate, ProductType } from "@/types";
import { fetchInterventionTemplateById, postInterventionTemplate, putInterventionTemplate } from "@/utils/intervention-template-api";
import { computed, onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const title = ref("");
const loading = ref(false);
const template = ref<InterventionTemplate>(({
  productType: null,
  items: [],
}));
const itemTypes = Object.values(InterventionItemType);
const productTypes = Object.values(ProductType);

const isCreatingNewTemplate = computed(
  () => route.params.id === "new"
);

function addItem() {
  template.value.items.push({
    label: "",
    itemType: InterventionItemType.CHECKBOX,
    displayOrder: template.value.items.length + 1,
    required: false,
  });
}

function removeItem(index) {
  template.value.items.splice(index, 1);
  reorder();
}

function moveUp(index) {
  if (index === 0) return;
  const items = template.value.items;
  [items[index - 1], items[index]] = [items[index], items[index - 1]];
  reorder();
}

function moveDown(index) {
  const items = template.value.items;
  if (index === items.length - 1) return;
  [items[index + 1], items[index]] = [items[index], items[index + 1]];
  reorder();
}

function reorder() {
  template.value.items.forEach((item, i) => {
    item.displayOrder = i + 1;
  });
}

async function submit() {
  try {
    loading.value = true;
    reorder();
    const templateData = { ...template.value };

    if (!templateData.id) {
      template.value = await postInterventionTemplate(templateData);

      appStore.sendSuccessNotice("New Template has been added.");
      router.replace({
        name: "InterventionTemplateEditor",
        params: { id: template.value.id },
      });
    } else {
      await putInterventionTemplate(templateData);
      appStore.sendSuccessNotice("Template has been updated.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  try {
    loading.value = true;
    if (isCreatingNewTemplate.value) {
      title.value = "Nouveau Template d'Intervention";
    } else {
      title.value = "Editer Template d'Intervention";
      const templateId = route.params.id as string;
      template.value = await fetchInterventionTemplateById(templateId);
    }
  } catch (error) {
  } finally {
    loading.value = false;
  }
})
</script>
